import { WhatsAppContentDefinition } from 'modules/graphql/types';
import { FormField } from 'ui/formField';
import { NegativeFill } from 'ui/icons';
import { colors } from 'ui/theme';
import { constructErrorInlineText, SettingsReadOnlyText } from '../../common';
import { WhatsAppRestrictionText } from '../../common/RestrictionText';
import { TextInputField } from '../../common/components';
import { FieldNames } from '../../common/types';
import { constructFieldErrors, getIsEditMode } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { MAX_FOOTER_LENGTH, SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useEditableWhatsApp } from '../hooks/useEditableWhatsApp';

type Props = { isReviewTemplateTakeover?: boolean };

export const FooterField = ({ isReviewTemplateTakeover = false }: Props) => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const { isEditableWhatsAppTemplate } = useEditableWhatsApp();

  const { footer } = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode) && isEditableWhatsAppTemplate;

  const restrictionTextLabel = (
    <WhatsAppRestrictionText textContent={footer || ''} maxChars={MAX_FOOTER_LENGTH} />
  );

  const { hasError: messageError, hasWarning: messageWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.FOOTER,
  });

  const messageErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: messageError,
    hasWarning: messageWarning,
  });

  const setFooter = (footerContent: string) => {
    // prevent accidentally saving an empty string
    if (footerContent.trim().length === 0) setCurrentMediumField('footer', null);
    else setCurrentMediumField('footer', footerContent);
  };

  if (isReviewTemplateTakeover && !footer) return null;

  return (
    <FormField
      data-test="whats-app-footer-form-field"
      errorText={messageErrorText}
      warningText={messageErrorText}
      hasError={messageError}
      hasWarning={messageWarning}
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.footer.labelText}
      restrictionText={restrictionTextLabel}
      helpText={isEditModeVisible && !messageError && 'Supports text and special characters'}
    >
      {isEditModeVisible ? (
        <TextInputField
          mediumDataKey="footer"
          mediumDataValue={footer}
          showSnippet={false}
          changeHandler={(footerContent: string) => setFooter(footerContent)}
          maxLength={MAX_FOOTER_LENGTH}
        />
      ) : (
        <SettingsReadOnlyText $hasError={messageError} isUnset={!footer}>
          {messageError && <NegativeFill color={colors.red100} />}
          {footer || SETTINGS_DISPLAY_CONTENT.unsetText}
        </SettingsReadOnlyText>
      )}
    </FormField>
  );
};
